import { ALERT_MIN, ALERT_MAX, IMAGE_SLOTS } from '@/utils/constants/rewardsDisplayConstants'
import { REWARD_TYPES } from '@/utils/constants/rewardsConstants'
import { Constants } from './constants/commonConstants'
import { getCurrencySymbolWithName } from './commons'

// Create an empty images array of length IMAGE_SLOTS
export const createEmptyImages = (count = IMAGE_SLOTS) =>
  Array.from({ length: count }, (_, i) => ({ position: i + 1, url: '', file: null }))

// Clamp alert display count to allowed bounds
export const clampAlertDisplayCount = (val, min = ALERT_MIN, max = ALERT_MAX) => {
  let num = Number(val)
  if (!Number.isFinite(num)) num = 0
  return Math.max(min, Math.min(max, num))
}

// Compute completion status for a productDisplay entry
export const getProductCompletion = (pd = {}, languages = [], rewardType) => {
  const imgs = pd?.images || []
  const imageOk = Array.isArray(imgs) && imgs.some(img => typeof img?.url === 'string' && img.url.trim().length > 0)
  const rewardText = pd?.rewardText || {}
  const langs = Array.isArray(languages) ? languages : []
  const textOk = langs.every(l => {
    const code = l.code || l
    const t = rewardText?.[code]?.title
    const d = rewardText?.[code]?.description
    const hasTitle = typeof t === 'string' && t.trim().length > 0
    const hasDesc = typeof d === 'string' && d.trim().length > 0
    return hasTitle && hasDesc
  })
  const requiresAlert = rewardType === REWARD_TYPES.BONUS_PRODUCTS
  const rawAdc = pd?.alertDisplayCount
  const hasAdcValue = rawAdc !== '' && rawAdc !== null && rawAdc !== undefined
  const parsedAdc = Number(rawAdc)
  const adcValid = Number.isFinite(parsedAdc) && parsedAdc >= 1 && parsedAdc <= ALERT_MAX
  const alertOk = requiresAlert ? (hasAdcValue && adcValid) : true
  const completed = Boolean(imageOk && textOk && alertOk)
  return { imageOk, textOk, alertOk, completed }
}

// Ensure defaults for a productDisplay entry, returns a merged object
export const ensureProductDisplayDefaults = (productId, display, effectiveProducts, currentPd = {}, defaultDescription = '') => {
  const images = currentPd?.images && currentPd.images.length > 0 ? currentPd.images : createEmptyImages()
  const rewardText = currentPd?.rewardText || {}

  const activeProduct = (effectiveProducts || []).find(p => p.value === productId)
  const defaultTitle = activeProduct?.label || ''

  // Build EN reward text defaults without forcing empty description
  const existingEn = rewardText?.en || {}
  const enDescription = (
    existingEn.description !== undefined && existingEn.description !== null && existingEn.description !== ''
  )
    ? existingEn.description
    : (defaultDescription || existingEn.description)

  const ensured = {
    useDefaultImage: Boolean(currentPd?.useDefaultImage),
    images,
    defaultImages: currentPd?.defaultImages || [],
    alertDisplayCount: typeof currentPd?.alertDisplayCount === 'number' ? clampAlertDisplayCount(currentPd.alertDisplayCount) : (currentPd?.alertDisplayCount ?? 0),
    shownOnHomepage: Boolean(currentPd?.shownOnHomepage),
    rewardText: {
      ...rewardText,
      en: {
        ...existingEn,
        title: existingEn.title ?? defaultTitle,
        ...(enDescription !== undefined ? { description: enDescription } : {}),
      },
    },
  }

  return ensured
}


// Helper function to get product display text
export const getProductDisplayText = (formData) => {
  if (!formData) return 'selected product'

  // Handle PHYSICAL and LOYALTY reward types first
  if (formData.rewardType === REWARD_TYPES.PHYSICAL) {
    return `[${Constants.PhysicalProduct}]`;
  }

  if (formData.rewardType === REWARD_TYPES.LOYALTY) {
    return `[${Constants.LoyaltyItem}]`;
  }

  // Select appropriate products array based on reward type
  // PRODUCT type uses selectedProducts, PEP_VOUCHER_PRODUCT uses selectedPepVouchersProducts
  const products = formData.rewardType === REWARD_TYPES.PRODUCT
    ? formData.selectedProducts
    : (formData.rewardType === REWARD_TYPES.PEP_VOUCHER_PRODUCT
      ? formData.selectedPepVouchersProducts
      : formData.selectedProducts)

  if (!products?.length) return 'selected product'

  if (products.length === 1) {
    const product = products[0];
    return product.label || product.productName || 'selected product';
  }

  return `${products.length} selected products`;
};

// Helper function to render bonus products preview
const renderBonusProductsPreview = (formData) => {
  if (formData.orderQuantity) {
    return `Get a bonus product after every ${formData.orderQuantity} orders`;
  }
  return '';
}

// Helper function to render product/PEP voucher product/physical preview
const renderProductPreview = (formData, prefix = '') => {
  if (formData.points) {
    return `Get ${prefix}${getProductDisplayText(formData)} when you redeem ${formData.points} points`;
  }
  return '';
}

// Helper function to render cash preview
const renderCashPreview = (formData, currencySymbol) => {
  // Support new value field (from incoming branch)
  if (formData.value && formData.points) {
    return `Get ${currencySymbol} ${formData.value} cash when you redeem ${formData.points} points.`;
  }
  // Fallback to rewardCashAmount field (from HEAD version)
  if (formData?.rewardCashAmount && (formData?.redeemPoints || formData?.points)) {
    return `Get ${currencySymbol} ${formData?.rewardCashAmount || '-'} cash when you redeem ${(formData?.redeemPoints || formData?.points || '-')} points`;
  }
  return '';
}

// Helper function to render discount preview
const renderDiscountPreview = (formData, currencySymbol) => {
  // Support new value field (from incoming branch)
  if (formData.value && formData.points) {
    return `Get ${currencySymbol} ${formData.value} Discount when you redeem ${formData.points} points.`;
  }
  // Fallback to discountAmount field (from HEAD version)
  if (formData?.discountAmount && (formData?.redeemPoints || formData?.points)) {
    return `Get ${currencySymbol} ${formData?.discountAmount || '-'} discount when you redeem ${(formData?.redeemPoints || formData?.points || '-')} points`;
  }
  return '';
}

// Helper function to render PEP voucher discount preview
const renderPepVoucherDiscountPreview = (formData, currencySymbol) => {
  if (formData.value && formData.points) {
    return `Get ${currencySymbol} ${formData.value} PepVoucher - Discount when you redeem ${formData.points} points.`;
  }
  return '';
}

// Helper function to render visa card preview
const renderVisaCardPreview = (formData, currencySymbol) => {
  if (formData?.rewardCashAmount && (formData?.redeemPoints || formData?.points)) {
    return `Get ${currencySymbol} ${formData?.rewardCashAmount || '-'} Visa Card when you redeem ${(formData?.redeemPoints || formData?.points || '-')} points`;
  }
  return '';
}

// Helper function to render points preview
const renderPointsPreview = (formData) => {
  if (formData.points) {
    return `Get ${formData.points} points as reward`;
  }
  return '';
}

// Helper function to render loyalty preview
const renderLoyaltyPreview = (formData) => {
  if (!formData.points) return '';
  
  // [Loyalty Item] is literal text, not a placeholder
  return `Get [Loyalty Item] when you redeem ${formData.points} points`;
}

// Helper function to render digital preview
const renderDigitalPreview = (formData, currencySymbol) => {
  if (formData.digitalValue && formData.digitalPoints && formData.digitalSupplier) {
    return `Get ${currencySymbol} ${formData.digitalValue} ${formData.digitalSupplier} gift card when you redeem ${formData.digitalPoints} points.`;
  }
  return '';
}

// Helper function to render preview based on reward type
export const renderPreviewContent = ({ formData, countryCode }) => {
  if (!formData) return '';

  const currencySymbol = countryCode ? getCurrencySymbolWithName(countryCode) : '$'

  switch (formData.rewardType) {
    case REWARD_TYPES.BONUS_PRODUCTS:
      return renderBonusProductsPreview(formData);

    case REWARD_TYPES.PRODUCT:
      return renderProductPreview(formData);

    case REWARD_TYPES.PEP_VOUCHER_PRODUCT:
      return renderProductPreview(formData, 'PepVoucher for ');

    case REWARD_TYPES.PHYSICAL:
      return renderProductPreview(formData);

    case REWARD_TYPES.CASH:
      return renderCashPreview(formData, currencySymbol);

    case REWARD_TYPES.DISCOUNT:
      return renderDiscountPreview(formData, currencySymbol);

    case REWARD_TYPES.PEP_VOUCHER_DISCOUNT:
      return renderPepVoucherDiscountPreview(formData, currencySymbol);

    case REWARD_TYPES.VISA_CARD:
      return renderVisaCardPreview(formData, currencySymbol);

    case REWARD_TYPES.POINTS:
      return renderPointsPreview(formData);

    case REWARD_TYPES.DIGITAL:
    case REWARD_TYPES.DIGITAL_PRE_ORDER: {
      return renderDigitalPreview(formData, currencySymbol);
    }

    case REWARD_TYPES.LOYALTY:
      return renderLoyaltyPreview(formData);

    default:
      return '';
  }
};
